import type {
  JobMatchNode,
  EditableGraph,
  Id,
  NodeVariables,
} from 'src/types/chatbot';
import type {EntityType} from 'src/types/ats-entities';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useEditorState, useChatbotVariables} from './reducer';
import {validateNode} from './validation';
import {filterValidation} from 'src/utils/validation';
import {keyBy} from 'src/utils/map';
import {
  findAncestorQuestions,
  findAncestorNodeVariables,
  getJobMatchCriterion,
  getMaxMatchesOptions,
  getNoMatchHandlingOptions,
} from 'src/components/conversation-builder/utils';
import {useWritebackMetadataV2} from 'src/hooks/useWritebackAllowedAttributes';

import {NodePopover} from './container';
import {FormPair, ButtonsSmall} from 'src/components/conversation-builder/form';
import {
  PadSection,
  useFirstSmsMessage,
  ColumnGrow,
  Table,
  TableHeader,
  TableBody,
  TableRowPadded,
} from './common';
import {TabGroup} from './tab-group';
import {MessageDraftEditor} from 'src/components/conversation-builder/draft-editor';
import TokenListInput, {Menu} from 'src/components/lib/token-list-input';
import Button from 'src/components/lib/new-button';
import LoadedVariablePicker from './variable-picker';
import {Toggle} from './node-link-toggle';

import css from 'src/components/conversation-builder/popover.css';


export default function JobMatchEditor({
  defaultNode,
  graph,
  hasSubmitted,
  entityType,
  onSubmit,
  edgeId,
  ...props
}: {
  entityType: EntityType;
  onClose: () => unknown;
  onSubmit: (arg1: JobMatchNode) => unknown;
  defaultNode: JobMatchNode;
  graph: EditableGraph;
  actionName: 'add' | 'edit';
  hasSubmitted: boolean;
  readOnly: boolean;
  edgeId?: Id;
}) {
  const {t} = useLabelTranslation();
  const writebackAttributes = useWritebackMetadataV2(entityType);
  const writebackAttributesByName = writebackAttributes
    ? keyBy(writebackAttributes, 'attribute_name')
    : {};

  const {readOnly, onClose, actionName} = props;

  const isFirstSmsMessage = useFirstSmsMessage(graph, defaultNode);
  const [state, dispatch] = useEditorState({
    defaultNode,
  });

  const update = (payload: {name: string}) =>
    dispatch({type: 'update', payload});
  const {node} = state;

  const parentNodeId = edgeId ? graph.edges.get(edgeId)?.from_node_id : null;
  const currentNode =
    edgeId != null && parentNodeId != null
      ? graph.nodes.get(parentNodeId)
      : defaultNode;

  const anscestorQuestions = findAncestorNodeVariables(
    graph,
    defaultNode,
    edgeId,
  );
  const nodeValueOptions = anscestorQuestions.map(
    ({derived_field_name, name}) => ({
      value: String(derived_field_name ?? name),
      label: name,
    }),
  );
  const ancestorDataByName = anscestorQuestions.reduce<Record<string, any>>(
    (result, nodeValue) => {
      const key = nodeValue.derived_field_name ?? nodeValue.name;
      result[key] = nodeValue;
      return result;
    },
    {},
  );

  const [jobMatchConfig, setJobMatchConfig] = React.useState(
    node.job_search_criteria_data,
  );

  const error = hasSubmitted
    ? validateNode({...node, job_search_criteria_data: jobMatchConfig}, graph)
    : null;

  const messageError = filterValidation(error, ['text_draft_json']);
  const noMatchHandlingOptions = getNoMatchHandlingOptions(t);
  const maxMatchesOptions = getMaxMatchesOptions(t);
  const jobMatchCriterion = getJobMatchCriterion(t);

  const tabs = [
    {
      id: 'criteria',
      title: t('Criteria', 'Criteria'),
      error: messageError,
      content: (
        <div className={css.table}>
          <PadSection>
            <p>{`${t(
              'Pick_a_node_value_or_variable_to_use_for_the_following_job_matching_criteria_If_a_node_value_is_left_blank_for_a_criteria_then_it_will_not_be_used_for_job_matching',
              'Pick a node value or variable to use for the following job matching criteria. If a node value is left blank for a criteria, then it will not be used for job matching',
            )}.`}</p>
          </PadSection>
          <Table>
            {error?.criteria?.errors?.includes('EMPTY') && (
              <p className={css.tableError}>
                {t(
                  'Please_include_at_least_one_job_match_criteria',
                  'Please include at least one job match criteria',
                )}
              </p>
            )}

            <TableHeader>
              <ColumnGrow>
                {t('Job_Match_Criteria', 'Job Match Criteria')}
              </ColumnGrow>
              <ColumnGrow>
                {graph.use_case === 'sourcing'
                  ? t('Pick_a_Node_Value', 'Pick a Node Value')
                  : `${t(
                      'Pick_a_Node_Value_or_ATS_variable',
                      'Pick a Node Value or ATS variable',
                    )}:`}
              </ColumnGrow>
            </TableHeader>
            <TableBody className={css.scrollable}>
              {jobMatchCriterion.map(({label, value}) =>
                jobMatchConfig.criteria[value] ? (
                  <InputRow
                    isSourcing={graph.use_case === 'sourcing'}
                    entityType={entityType}
                    name={label}
                    nodeIds={mapReferenceNodeIdValues(
                      jobMatchConfig.criteria[value].reference_node_ids,
                      anscestorQuestions,
                      ancestorDataByName,
                    )}
                    atsVariables={jobMatchConfig.criteria[
                      value
                    ].ats_variables.map((atsVariable) => ({
                      value: atsVariable,
                      label: `<${atsVariable}>`,
                    }))}
                    nodeIdOptions={nodeValueOptions || []}
                    handleSelectToken={(tokens) => {
                      const nodes: Array<{
                        label: string;
                        value: string;
                      }> = [];
                      const vars: Array<{
                        label: string;
                        value: string;
                      }> = [];

                      tokens.forEach((token) => {
                        if (/<(.*)>/g.test(token.label)) {
                          vars.push(token);
                        } else {
                          nodes.push(token);
                        }
                      });

                      const criteria = {...jobMatchConfig.criteria} as const;
                      // @ts-expect-error - TS2540 - Cannot assign to 'compensation_amount_min' because it is a read-only property. | TS2540 - Cannot assign to 'compensation_currency' because it is a read-only property. | TS2540 - Cannot assign to 'compensation_frequency' because it is a read-only property. | TS2540 - Cannot assign to 'job_title' because it is a read-only property. | TS2540 - Cannot assign to 'keywords' because it is a read-only property. | TS2540 - Cannot assign to 'location' because it is a read-only property. | TS2540 - Cannot assign to 'skills' because it is a read-only property. | TS2540 - Cannot assign to 'state' because it is a read-only property. | TS2540 - Cannot assign to 'worksite_options' because it is a read-only property.
                      criteria[value] = {
                        ...criteria[value],
                        reference_node_ids: nodes.map((option) => ({
                          node_id: ancestorDataByName[option.value]?.node_id,
                          field_name: option.value,
                        })),
                        ats_variables: vars.map((option) => option.value),
                      };
                      setJobMatchConfig({
                        ...jobMatchConfig,
                        criteria,
                      });
                    }}
                  />
                ) : null,
              )}
            </TableBody>
          </Table>
        </div>
      ),
    },
    {
      id: 'message',
      title: t('Message', 'Message'),
      error: messageError,
      content: (
        <PadSection>
          {error?.message?.errors?.includes('MISSING') && (
            <p className={css.tableError}>
              {t(
                'Please_fill_out_all_required_fields',
                'Please fill out all required fields',
              )}
            </p>
          )}
          <MessageDraftEditor
            label={`${t(
              'If_matching_jobs_are_available_show_this_message',
              'If matching jobs are available, show this message',
            )}*:`}
            placeholder={`${t(
              'We_found_job_matches_for_you_Here_they_are',
              'We found job matches for you! Here they are',
            )}:`}
            defaultValue={jobMatchConfig.matching_messages_rte}
            onChange={(matching_messages_rte) =>
              setJobMatchConfig({...jobMatchConfig, matching_messages_rte})
            }
            entityType={entityType}
            isFirstSmsMessage={isFirstSmsMessage}
            error={error?.matching_message}
            readOnly={Boolean(readOnly)}
          />
          <FormPair
            label={` ${t(
              'Maximum_number_of_matches_to_show',
              'Maximum number of matches to show',
            )}: `}
          >
            <Menu
              value={jobMatchConfig.max_matches}
              options={maxMatchesOptions}
              onChange={(option) =>
                setJobMatchConfig({
                  ...jobMatchConfig,
                  max_matches: option.value,
                })
              }
              disabled={readOnly}
            />
          </FormPair>
          <FormPair
            label={`${t(
              'If_no_matches_are_available',
              'If no matches are available',
            )}:`}
          >
            <Menu
              value={jobMatchConfig.no_matches_handling}
              options={noMatchHandlingOptions}
              onChange={(option) =>
                setJobMatchConfig({
                  ...jobMatchConfig,
                  no_matches_handling: option.value,
                })
              }
              disabled={readOnly}
            />
          </FormPair>

          <MessageDraftEditor
            label={`${t('Message', 'Message')}*:`}
            placeholder={`${t(
              'Sorry_we_didn\'t_find_any_matches_but_here_are_some_other_available_jobs',
              'Sorry, we didn\'t find any matches, but here are some other available jobs',
            )}:`}
            defaultValue={jobMatchConfig.no_matches_message_rte}
            onChange={(no_matches_message_rte) =>
              setJobMatchConfig({...jobMatchConfig, no_matches_message_rte})
            }
            entityType={entityType}
            isFirstSmsMessage={isFirstSmsMessage}
            error={error?.no_matches_message}
            readOnly={Boolean(readOnly)}
          />
        </PadSection>
      ),
    },
  ];

  const [currentTab, setCurrentTab] = React.useState('criteria');

  const tabIndex = tabs.findIndex((tab) => tab.id === currentTab);
  const isLastTab = tabIndex === tabs.length - 1;

  const handleSubmit = () => {
    if (actionName === 'add' && !isLastTab) {
      setCurrentTab(tabs[tabIndex + 1].id);
    } else {
      onSubmit({...state.node, job_search_criteria_data: jobMatchConfig});
    }
  };

  const hasError = Boolean(error);
  React.useEffect(() => {
    if (hasError && hasSubmitted) {
      if (error?.criteria) {
        setCurrentTab('criteria');
      } else {
        setCurrentTab('message');
      }
    }
  }, [hasError, hasSubmitted]);

  return (
    <NodePopover
      onChangeName={(name) => update({name})}
      state={state}
      error={error}
      {...props}
    >
      <TabGroup currentTab={currentTab} onChange={setCurrentTab} tabs={tabs}>
        <PadSection>
          <ButtonsSmall>
            <Button size="medium" onClick={onClose}>
              {readOnly ? t('Done', 'Done') : t('Cancel', 'Cancel')}
            </Button>
            {!readOnly && (
              <Button
                type="primary"
                size="medium"
                onClick={handleSubmit}
                disabled={Boolean(error)}
              >
                {actionName === 'edit'
                  ? t('Done', 'Done')
                  : isLastTab
                  ? t('Add', 'Add')
                  : t('Next', 'Next')}
              </Button>
            )}
          </ButtonsSmall>
        </PadSection>
      </TabGroup>
    </NodePopover>
  );
}

function InputRow({
  name,
  nodeIds,
  nodeIdOptions,
  entityType,
  isSourcing,
  atsVariables,
  handleSelectToken,
}: {
  name: string;
  nodeIds: {
    value: string;
    label: string;
  }[];
  nodeIdOptions: {
    value: string;
    label: string;
  }[];
  handleSelectToken: (
    arg1: {
      value: string;
      label: string;
    }[],
  ) => void;
  isSourcing: boolean;
  atsVariables: {
    value: string;
    label: string;
  }[];
  entityType: EntityType;
}) {
  const writebackAttributes = useChatbotVariables(entityType);

  const writebackOptions =
    writebackAttributes.map((data) => ({
      label: `<${data.attributeName ?? data.name}>`,
      value: data.attributeName ?? data.value,
    })) ?? [];
  const tokenOptions = isSourcing
    ? nodeIdOptions
    : [...nodeIdOptions, ...writebackOptions];
  const selectedTokens = isSourcing ? nodeIds : [...nodeIds, ...atsVariables];

  return (
    <TableRowPadded>
      <ColumnGrow className={css.tableContent}>{name}</ColumnGrow>
      <ColumnGrow className={css.input}>
        <TokenListInput
          values={selectedTokens || []}
          allowArbitraryValues={false}
          options={tokenOptions}
          // @ts-expect-error - TS2769 - No overload matches this call.
          onChange={handleSelectToken}
          openOnFocus={true}
          containerClassName={css.input}
          searchable
          isExpandable
        />
      </ColumnGrow>
    </TableRowPadded>
  );
}

const mapReferenceNodeIdValues = (
  nodeValues: Array<{
    field_name: string;
    node_id?: Id;
  }>,
  anscestorQuestions: NodeVariables,
  ancestorDataByName: Record<any, any>,
) => {
  if (typeof nodeValues[0] === 'object') {
    return nodeValues.map(({field_name}) => ({
      value: field_name,
      label: String(ancestorDataByName[field_name]?.name),
    }));
  } else {
    return nodeValues.map((id) => {
      const nodeName =
        // @ts-expect-error - TS7006 - Parameter 'node' implicitly has an 'any' type.
        anscestorQuestions.find((node) => node.node_id === id)?.name ?? '';
      return {
        value: nodeName,
        label: nodeName,
      };
    });
  }
};
